import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {atom, useRecoilState} from 'recoil';

import {getRouteUrl} from '_/utilities';

type ReturnType = {
  cockpitPath: string;
  casusPath: string;
  contactsPath: string;
  communicationPath: string;
  timeTrackingPath: string;
  invoicingPath: string;
  searchPath: string;
  settingsPath: string;
};

export const useLastModulePathCache = (): ReturnType => {
  const [pathCache, setPathCache] = useRecoilState(lastModulePathCache);
  const location = useLocation();

  const updatePathCache = (moduleToUpdate: keyof LastModulePathCache): void => {
    const fullPath = location.pathname + location.search;
    if (fullPath !== pathCache[moduleToUpdate]) {
      setPathCache((cachedPaths) => ({
        ...cachedPaths,
        [moduleToUpdate]: fullPath,
      }));
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith(cockpitRootPath)) {
      updatePathCache('cachedCockpitPath');
    } else if (location.pathname.startsWith(contactsRootPath)) {
      updatePathCache('cachedContactsPath');
    } else if (location.pathname.startsWith(communicationRootPath)) {
      updatePathCache('cachedCommunicationPath');
    } else if (location.pathname.startsWith(timeTrackingRootPath)) {
      updatePathCache('cachedTimeTrackingPath');
    } else if (location.pathname.startsWith(invoicingRootPath)) {
      updatePathCache('cachedInvoicingPath');
    } else if (location.pathname.startsWith(searchRootPath)) {
      updatePathCache('cachedSearchPath');
    } else if (location.pathname.startsWith(settingsRootPath)) {
      updatePathCache('cachedSettingsPath');
    }
    // update path cache only on location change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return {
    cockpitPath: pathCache.cachedCockpitPath ?? cockpitRootPath,
    casusPath: casusRootPath,
    contactsPath: pathCache.cachedContactsPath ?? contactsRootPath,
    communicationPath:
      pathCache.cachedCommunicationPath ?? communicationRootPath,
    timeTrackingPath: pathCache.cachedTimeTrackingPath ?? timeTrackingRootPath,
    invoicingPath: pathCache.cachedInvoicingPath ?? invoicingRootPath,
    searchPath: pathCache.cachedSearchPath ?? searchRootPath,
    settingsPath: pathCache.cachedSettingsPath ?? settingsRootPath,
  };
};

const cockpitRootPath = getRouteUrl({module: 'cockpit'});
const casusRootPath = getRouteUrl({module: 'casus'});
const contactsRootPath = getRouteUrl({module: 'contacts'});
const communicationRootPath = getRouteUrl({module: 'communication'});
const timeTrackingRootPath = getRouteUrl({module: 'time-tracking'});
const invoicingRootPath = getRouteUrl({module: 'invoicing'});
const searchRootPath = getRouteUrl({module: 'search'});
const settingsRootPath = getRouteUrl({module: 'settings'});

type LastModulePathCache = {
  cachedCockpitPath?: string;
  cachedContactsPath?: string;
  cachedCommunicationPath?: string;
  cachedTimeTrackingPath?: string;
  cachedInvoicingPath?: string;
  cachedSearchPath?: string;
  cachedSettingsPath?: string;
};

export const lastModulePathCache = atom<LastModulePathCache>({
  key: 'lastModulePathCache',
  default: {},
});
